import { UserPotClaim } from '../pot/types';
import { apiClient } from '../../api/client';
import { ApiResponse, PagedResponse } from '../../api/types';
import {
  AdminUser,
  AdminUserDetail,
  CreateUserInput,
  UpdateUserInput,
  UserRole,
  UserStatus,
  UserMatch,
  UserInventoryItem,
} from './types';

export interface ListUsersParams {
  page: number;
  search?: string;
  role?: UserRole;
  status?: UserStatus;
}

export async function listUserInventory(id: string): Promise<UserInventoryItem[]> {
  const response = await apiClient.get<ApiResponse<UserInventoryItem[]>>(`/api/v1/admin/users/${encodeURIComponent(id)}/inventory`);
  return response.data.data;
}

export async function listUserMatches(id: string, page: number, perPage: number): Promise<PagedResponse<UserMatch>> {
  const response = await apiClient.get<PagedResponse<UserMatch>>(`/api/v1/admin/users/${encodeURIComponent(id)}/matches`, { params: { page, per_page: perPage } });
  return response.data;
}

export async function getUser(id: string): Promise<AdminUserDetail> {
  const response = await apiClient.get<ApiResponse<AdminUserDetail>>(
    `/api/v1/admin/users/${encodeURIComponent(id)}`
  );
  return response.data.data;
}

export async function listUsers(
  params: ListUsersParams
): Promise<PagedResponse<AdminUser>> {
  const response = await apiClient.get<PagedResponse<AdminUser>>(
    '/api/v1/admin/users',
    {
      params: {
        page: params.page,
        per_page: 10,
        ...(params.search?.trim() ? { search: params.search.trim() } : {}),
        ...(params.role ? { role: params.role } : {}),
        ...(params.status ? { status: params.status } : {}),
      },
    }
  );
  return response.data;
}

export async function createUser(input: CreateUserInput): Promise<AdminUser> {
  const response = await apiClient.post<ApiResponse<AdminUser>>(
    '/api/v1/admin/users',
    input
  );
  return response.data.data;
}

export async function updateUser(
  id: string,
  input: UpdateUserInput
): Promise<AdminUser> {
  const response = await apiClient.patch<ApiResponse<AdminUser>>(
    `/api/v1/admin/users/${encodeURIComponent(id)}`,
    input
  );
  return response.data.data;
}

export async function deleteUser(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/admin/users/${encodeURIComponent(id)}`);
}

export async function promoteUser(id: string): Promise<AdminUser> {
  const response = await apiClient.patch<ApiResponse<AdminUser>>(
    `/api/v1/admin/users/${encodeURIComponent(id)}/promote`
  );
  return response.data.data;
}

export async function setUserStatus(
  id: string,
  status: UserStatus
): Promise<AdminUser> {
  const action = status === 'active' ? 'activate' : 'deactivate';
  const response = await apiClient.patch<ApiResponse<AdminUser>>(
    `/api/v1/admin/users/${encodeURIComponent(id)}/${action}`
  );
  return response.data.data;
}

export async function resetUserIAPPurchases(id: string): Promise<void> {
  await apiClient.post(
    `/api/v1/admin/users/${encodeURIComponent(id)}/iap-purchases/reset`
  );
}

export async function recalculateUserPaySegment(id: string): Promise<void> {
  await apiClient.post(
    `/api/v1/admin/users/${encodeURIComponent(id)}/pay-segment/recalculate`
  );
}

export async function resetUserProgress(id: string): Promise<void> {
  await apiClient.post(`/api/v1/admin/users/${encodeURIComponent(id)}/reset`);
}

export async function listUserPotClaims(id: string, page: number, perPage: number): Promise<PagedResponse<UserPotClaim>> {
  const response = await apiClient.get<PagedResponse<UserPotClaim>>(`/api/v1/admin/users/${encodeURIComponent(id)}/pot-claims`, {
    params: { page, per_page: perPage },
  });
  return response.data;
}
